import React, { useState } from 'react';
import { Card, CardBody } from './Card';
import { Badge } from './Badge';

interface ModelResult {
  modelType: string;
  confidence: number;
  roofArea: number;
  sidingArea: number;
  imageUrl?: string;
  overlayUrl?: string;
}

interface ModelComparisonViewProps {
  baselineModel?: ModelResult;
  trainedModel?: ModelResult;
  satelliteImageUrl?: string;
}

export const ModelComparisonView: React.FC<ModelComparisonViewProps> = ({
  baselineModel,
  trainedModel,
  satelliteImageUrl
}) => {
  const [overlayOpacity, setOverlayOpacity] = useState(0.6);

  const currentModel = trainedModel;
  const hasComparison = false;

  return (
    <div style={{ marginTop: 'var(--spacing-6)' }}>
      <h3 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-4)' }}>
        AI Analysis Visualization
      </h3>


      <div style={{ display: 'grid', gridTemplateColumns: hasComparison ? 'repeat(auto-fit, minmax(300px, 1fr))' : '1fr', gap: 'var(--spacing-6)' }}>
        {/* Satellite Image with Overlay */}
        <Card>
          <CardBody>
            <div style={{ marginBottom: 'var(--spacing-3)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-2)' }}>
                <h4 style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)' }}>
                  Satellite Imagery
                </h4>
                {currentModel && (
                  <Badge variant={currentModel.confidence > 0.8 ? 'success' : currentModel.confidence > 0.6 ? 'warning' : 'secondary'}>
                    {(currentModel.confidence * 100).toFixed(1)}% Confidence
                  </Badge>
                )}
              </div>
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-600)' }}>
                {currentModel?.modelType || 'AI Model'} Detection
              </p>
            </div>

            <div style={{ position: 'relative', width: '100%', aspectRatio: '1', backgroundColor: 'var(--color-gray-100)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
              {satelliteImageUrl ? (
                <>
                  <img 
                    src={satelliteImageUrl} 
                    alt="Satellite imagery"
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  />
                  {currentModel?.overlayUrl && (
                    <div 
                      style={{ 
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%', 
                        height: '100%',
                        opacity: overlayOpacity,
                        pointerEvents: 'none',
                        backgroundImage: `url(${currentModel.overlayUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }}
                    />
                  )}
                </>
              ) : (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  height: '100%',
                  color: 'var(--color-gray-400)'
                }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                  <p style={{ marginTop: 'var(--spacing-2)', fontSize: 'var(--text-sm)' }}>
                    Satellite imagery not available
                  </p>
                </div>
              )}
            </div>

            {currentModel?.overlayUrl && (
              <div style={{ marginTop: 'var(--spacing-4)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
                  <label style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', minWidth: '80px' }}>
                    Overlay Opacity:
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={overlayOpacity}
                    onChange={(e) => setOverlayOpacity(parseFloat(e.target.value))}
                    style={{ flex: 1, cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', minWidth: '40px' }}>
                    {(overlayOpacity * 100).toFixed(0)}%
                  </span>
                </div>
              </div>
            )}

            <div style={{ marginTop: 'var(--spacing-4)', paddingTop: 'var(--spacing-4)', borderTop: '1px solid var(--color-gray-200)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', fontSize: 'var(--text-xs)', color: 'var(--color-gray-600)' }}>
                <div style={{ width: '12px', height: '12px', backgroundColor: 'rgba(255, 0, 0, 0.6)', borderRadius: '2px' }}></div>
                <span>Roof Detection</span>
                <div style={{ width: '12px', height: '12px', backgroundColor: 'rgba(0, 255, 0, 0.6)', borderRadius: '2px', marginLeft: 'var(--spacing-3)' }}></div>
                <span>Siding Detection</span>
              </div>
            </div>
          </CardBody>
        </Card>

      </div>
    </div>
  );
};
